import { cacheAllModuleFiles, updateModuleCache } from '@/store/sync/cachingUtils';
import moduleIndex from './index.js';
import { performInMemorySearch } from '@/components/search/searchStore';
import moduleId from './moduleId';
import unfalldeskApi from './unfalldeskApi';
import { SYNC_STATES } from '@/store/sync/sync';
import i18n from '@/plugins/i18n';

const ROOT = { root: true };
const TAG = '@/modules/unfalldesk';
let cache;
const CACHE_REQUEST_NAME = '/medias/unfalldesk/tray';

const state = {
  udConfig: undefined,
  udEigenschaden: undefined,
  udFahrgastunfall: undefined,
  udProtokoll: undefined,
  skizzenByFzName: [],
  udDocuments: [],
  fahrzeuge: [],
  fzByTyp: {},
  trayDate: undefined, //Used to identify the currently active tray entry
  trayUuid: undefined, //Used to identify the currently active tray entry
  tray: [],
  dataLoaded: false,
  active: { name: 'udConfig', index: 0 },
  formInput: {},
  closeDialogOpen: false,
  toRoute: undefined, //Used by navigation hook to close the form first
};
const getters = {
  /** -------------------------------------------- ----------------------------------------------------------------------
   * Module Implementation for unread badge counts by instance.
   * IMPLEMENT this getter to enable unread counter badges
   * -------------------------------------------------------------------------------------------------------------------
   */
  getUnreadCountByModuleInstance: (state) => (instance) => {
    // module does not support instance
    return 0;
  },
};
const mutations = {
  setData(state, serverData) {
    Object.keys(serverData).forEach((key) => {
      state[key] = Object.freeze(serverData[key]);
    });
    state.dataLoaded = true;
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for UPDATING Module counters
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  countModuleItems(state) {},
  async setTray(state, tray) {
    state.tray = tray;
    const blob = new Blob([JSON.stringify(tray, null, 2)], { type: 'application/json' });
    const response = new Response(blob, { status: 200 });
    cache.put(new Request(CACHE_REQUEST_NAME), response);
  },
  async initStore(state) {
    cache = await caches.open('unfalldesk/tray');
    let cachedTray = await cache.match(new Request(CACHE_REQUEST_NAME));
    if (cachedTray) {
      state.tray = await cachedTray.json();
    }
  },
  toggleCloseDialog(state, open) {
    state.closeDialogOpen = open;
    if (!open) {
      state.toRoute = undefined;
    }
  },
  setToRoute(state, route) {
    state.toRoute = route;
  },
};

const actions = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for IN-MEMORY SEARCH
   * IMPLEMENT this getter to enable module in-memory search
   * Call the action in search/searchStore (search for "Module in-memory searches" there)
   * Search all documents containing all of the space separated search words in state.filterCriteria
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  searchModuleInMemoryContainingSearchTerm({ state }, { filterCriteria, options }) {
    return performInMemorySearch({
      moduleId,
      items: undefined, //TODO: define searchable state
      titleSearchAttr: 'name',
      contentSearchAttr: null,
      searchCriteriaArray: filterCriteria,
      options,
    });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Updates the browsers cache storage oevpad-api/modules/{moduleId} with the exact same data structure as received by the module sync api call!
   * Reason: This mutation MUST be called whenever data has changed, e.g. user_state data. It assures that the application cache is up-to-date
   *         when the user synchronized/updates in offline mode.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async updateModuleApplicationCache({ state }) {
    //TODO: add cache
    //const {  } = state;
    //await updateModuleCache(moduleIndex.api, { reports, fzByTyp, klassifizierungen, skizzenByFzName, ereignisse, wbConfig });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  // async synchronizeFiles({ commit, dispatch, state }) {
  //   //await cacheAllModuleFiles({ files: state.reports, filenameAttribute: 'pdf', moduleIndex, commit, dispatch });
  // },
  async synchronizeFiles({ commit, dispatch, state }) {
    let skizzenFiles = state.skizzenByFzName.map((f) => {
      // return { filename: f.filename, checksum: f.checksum,requestSuffix:'/wagenbuch-fzg' };
      return { filename: f.filename, checksum: f.checksum, requestSuffix: '/wagenbuch-fzg' };
    });

    // udConfig should follow (html cell entry). However currently, these files are stored in the assets folder

    let files = skizzenFiles.concat(state.udDocuments);
    await cacheAllModuleFiles({ files, moduleIndex: moduleIndex, commit, dispatch });
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeData({ commit, rootState, rootGetters }) {
    try {
      const response = await unfalldeskApi.getData();
      let { udConfig, udEigenschaden, udFahrgastunfall, udProtokoll, udDocuments, fzByTyp, fahrzeuge, skizzenByFzName } = response.data;

      commit('setData', {
        udConfig,
        udEigenschaden,
        udFahrgastunfall,
        udProtokoll,
        udDocuments,
        fzByTyp,
        fahrzeuge,
        skizzenByFzName,
      });
    } catch (err) {
      console.error(err);
      commit('sync/setSyncState', { moduleId, syncState: SYNC_STATES.FAILURE, msg: err }, ROOT);
      throw err;
    }
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Dispatched in store/sync !!!
   * Gets dispatched after all sync processes have been completed.
   * This is the place to implement any post sync processes.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async onSyncComplete({ commit, dispatch, rootState, rootGetters }) {
    // Implement this method if necessary
  },

  async sendForm({ commit, dispatch }, { instance, module, formData }) {
    // add to sendbox
    console.debug('sendForm Unfalldesk', formData);
    const text = i18n.t('action.submitted.msg', { object: i18n.t('module.unfalldesk.unfallmeldung') });
    const title = `Unfallmeldung '${formData.type}' eingereicht`;
    const action = 'STORE_UNFALLDESK';
    const subject = {
      ...formData,
      userdatemodified: new Date(),
      _type: 'unfallmeldung',
    };
    await dispatch('sendbox/performSendboxItem2Action', { subject, moduleId, title, text, action }, ROOT);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
